import { initializeApollo } from "../apiService/apolloClient";
import {
  CREATE_VALUATION_FORM_VALUES,
  GET_VALUATION_FORM_VALUES,
  VALUATION_TEMPLATE_QUERY,
  UPDATE_VALUATION_FORM_VALUES
} from "../graphql/valuationForm";
import { getFormTemplate, transformTemplatePanels } from "./formTemplates";

export { getFormTemplate, transformTemplatePanels };

export const transformValuationFormValues = arr =>
  arr?.reduce((acc, value) => {
    acc[value.fieldUI] = JSON.parse(value.value);
    return acc;
  }, {});

// The version the backend assigned after a save (create or update).
export const getSavedVersion = response =>
  response?.data?.updateValuationFormValues?.version ??
  response?.data?.createValuationFormValues?.version ??
  null;

// Apollo Client 4 rejects mutations with CombinedGraphQLErrors (`errors`);
// older shapes exposed `graphQLErrors`. Either way the backend tags a stale
// save with extensions.code = "STALE_VERSION".
export const isStaleVersionError = error => {
  const errors = error?.errors ?? error?.graphQLErrors ?? [];
  return errors.some(e => e?.extensions?.code === "STALE_VERSION");
};

// Turns raw template panels into what the renderer consumes: deep-cloned,
// each `meta` JSON string spread onto its owner, null-valued field keys
// dropped. Pure, so tests can mount exactly what the app mounts.

const BanksService = {
  async getBankValuationForm(id, bankName, formType) {
    const apolloClient = initializeApollo();
    let response;
    // When form is added to the db, remove this check and just run the query
    const isFormOnDB = false;
    if (isFormOnDB) {
      try {
        response = await apolloClient.query({
          query: VALUATION_TEMPLATE_QUERY,
          variables: { id }
        });
      } catch (e) {
        response = getFormTemplate(bankName, formType);
      }
    }

    response = getFormTemplate(bankName, formType);

    const valuationForm = transformTemplatePanels(response?.data.valuationForm?.panels);

    return valuationForm;
  },
  // `version` is the optimistic-locking counter loaded with the values. The
  // backend rejects a save whose version is behind the row's with a
  // STALE_VERSION error (see isStaleVersionError); omit it to skip the check.
  async saveValuationForm({ id, valuationId, values, revalidation, version }) {
    const objectToArray = obj =>
      obj
        ? Object.keys(obj).map(key => ({
            fieldUI: key,
            value: JSON.stringify(obj[key])
          }))
        : [];

    const apolloClient = initializeApollo();

    const valuesInput = {
      id,
      valuationId,
      valuationFormValues: objectToArray(values),
      revalidation: objectToArray(revalidation),
      ...(id && version != null ? { version } : {})
    };
    let mutationResponse;
    if (id) {
      // If we have id, we are updating a previous created one.
      mutationResponse = await apolloClient.mutate({
        mutation: UPDATE_VALUATION_FORM_VALUES,
        variables: {
          input: valuesInput
        }
      });
    } else {
      // If we don't have id, we should create a new valuation form
      mutationResponse = await apolloClient.mutate({
        mutation: CREATE_VALUATION_FORM_VALUES,
        variables: {
          input: valuesInput
        }
      });
    }
    return mutationResponse;
  },
  async getValuationFormValues(id, setError) {
    try {
      const apolloClient = initializeApollo();
      const response = await apolloClient.query({
        query: GET_VALUATION_FORM_VALUES,
        variables: { id: id },
        fetchPolicy: "no-cache"
      });

      const responseId = response?.data?.valuationFormValues?.id;
      const version = response?.data?.valuationFormValues?.version ?? null;
      const revalidation = response?.data?.valuationFormValues?.revalidation;
      const values = response?.data?.valuationFormValues?.valuationFormValues;

      return {
        id: responseId,
        version,
        savedValues: transformValuationFormValues(values),
        revalidation: transformValuationFormValues(revalidation)
      };
    } catch (error) {
      setError(error.message);
      console.error("getValuationFormValues error", error);
    }
  }
};

export default BanksService;
